import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { useCursorInfinite } from "../api/useCursorInfinite";
import type {
  BriefingHistoryResponse,
  BriefingRisk,
  BriefingTheme,
  CategorySummary,
} from "../api/types";
import { clockTime } from "../components/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "@/components/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { InfiniteScrollSentinel } from "@/components/InfiniteScrollSentinel";
import { useLanguage } from "@/i18n/useLanguage";
import { LanguageBadge } from "@/i18n/LanguageBadge";

interface BriefingsModalProps {
  open: boolean;
  onClose: () => void;
  onClusterJump: (clusterId: string) => boolean;
}

function categoryTime(category: CategorySummary): string {
  return clockTime(category.current_briefing_created_at);
}

function ClusterButton({
  clusterId,
  onClusterClick,
}: {
  clusterId: string;
  onClusterClick: (clusterId: string) => void;
}) {
  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 font-mono text-xs"
      onClick={() => onClusterClick(clusterId)}
      type="button"
    >
      {clusterId}
    </Button>
  );
}

function ThemeRow({
  theme,
  onClusterClick,
}: {
  theme: BriefingTheme;
  onClusterClick: (clusterId: string) => void;
}) {
  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <strong className="text-sm">{theme.theme}</strong>
        <span className="text-xs text-muted-foreground">{clockTime(theme.last_updated_at)}</span>
      </div>
      <p className="text-sm">{theme.summary}</p>
      <div className="flex flex-wrap gap-1.5">
        {theme.supporting_cluster_ids.map((clusterId) => (
          <ClusterButton clusterId={clusterId} key={clusterId} onClusterClick={onClusterClick} />
        ))}
      </div>
    </Card>
  );
}

function RiskRow({
  risk,
  onClusterClick,
}: {
  risk: BriefingRisk;
  onClusterClick: (clusterId: string) => void;
}) {
  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <strong className="text-sm">{risk.risk}</strong>
        <span className="text-xs text-muted-foreground">{risk.severity} · {risk.horizon}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {risk.supporting_cluster_ids.map((clusterId) => (
          <ClusterButton clusterId={clusterId} key={clusterId} onClusterClick={onClusterClick} />
        ))}
      </div>
    </Card>
  );
}

interface OlderVersionsSectionProps {
  pages: BriefingHistoryResponse[];
  isLoading: boolean;
  isFetchingNext: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  /** Version number of the briefing rendered above; we filter it out of the
   * "older versions" list so the page-0 row isn't shown twice. */
  currentVersion: number;
  onClusterClick: (clusterId: string) => void;
}

function OlderVersionsSection({
  pages,
  isLoading,
  isFetchingNext,
  hasNextPage,
  onLoadMore,
  currentVersion,
  onClusterClick,
}: OlderVersionsSectionProps) {
  const { t } = useTranslation("dashboard");
  const items = pages.flatMap((p) => p.items).filter((i) => i.version !== currentVersion);
  if (!isLoading && items.length === 0 && !hasNextPage) {
    return null;
  }
  return (
    <section className="space-y-2 border-t border-border pt-3">
      <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
        {t("briefings.olderVersions")}
      </h3>
      {items.map((item) => (
        <details key={item.version} className="rounded-md border border-border">
          <summary className="cursor-pointer select-none px-3 py-2 text-sm">
            <span className="font-mono">v{item.version}</span>{" "}
            <span className="text-muted-foreground">
              · {clockTime(item.created_at)}
              {item.trigger_kind ? ` · ${item.trigger_kind}` : ""}
            </span>
          </summary>
          <div className="space-y-2 px-3 pb-3 text-xs">
            {item.body_json.key_themes?.length > 0 && (
              <div>
                <div className="mt-1 text-muted-foreground">{t("briefings.themes")}</div>
                {item.body_json.key_themes.map((theme) => (
                  <ThemeRow
                    key={`${item.version}-${theme.theme}`}
                    theme={theme}
                    onClusterClick={onClusterClick}
                  />
                ))}
              </div>
            )}
            {item.body_json.active_risks?.length > 0 && (
              <div>
                <div className="mt-2 text-muted-foreground">{t("briefings.risks")}</div>
                {item.body_json.active_risks.map((risk) => (
                  <RiskRow
                    key={`${item.version}-${risk.risk}`}
                    risk={risk}
                    onClusterClick={onClusterClick}
                  />
                ))}
              </div>
            )}
          </div>
        </details>
      ))}
      {isFetchingNext && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("briefings.loadingOlder")}
        </div>
      )}
      <InfiniteScrollSentinel
        onVisible={onLoadMore}
        disabled={!hasNextPage || isFetchingNext}
      />
    </section>
  );
}

export function BriefingsModal({ open, onClose, onClusterJump }: BriefingsModalProps) {
  const { t } = useTranslation("dashboard");
  const { language } = useLanguage();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const categoriesQuery = useQuery({
    queryKey: ["categories"],
    queryFn: api.listCategories,
    enabled: open,
  });
  const categories = categoriesQuery.data?.categories ?? [];
  // i18n Slice 2 — request the user's preferred language; the API returns
  // a full envelope { body, language_served, ... } so we can render the
  // "Available in English" badge when the language falls back to EN.
  const briefingQuery = useQuery({
    queryKey: ["category-briefing", selectedCategoryId, language],
    queryFn: () => api.getCategoryBriefing(selectedCategoryId ?? "", language),
    enabled: open && selectedCategoryId != null,
  });
  // Older versions of the briefing — cursor-paginated. Disabled until a
  // category is selected so we don't fire requests against `""`. Pass
  // `lang` so the history matches the language of the current briefing.
  const historyQuery = useCursorInfinite<BriefingHistoryResponse>({
    queryKey: ["category-briefing-history", selectedCategoryId, language],
    fetchPage: (cursor) =>
      api.listCategoryBriefingsPage(selectedCategoryId ?? "", {
        ...cursor,
        lang: language,
      }),
    enabled: open && selectedCategoryId != null,
    limit: 10,
  });

  useEffect(() => {
    if (!open) return;
    if (!selectedCategoryId && categories.length > 0) {
      setSelectedCategoryId(categories[0].category_id);
    }
  }, [categories, open, selectedCategoryId]);

  useEffect(() => {
    if (!open) {
      setMessage(null);
      setSelectedCategoryId(null);
    }
  }, [open]);

  const handleClusterClick = (clusterId: string) => {
    if (onClusterJump(clusterId)) {
      onClose();
      return;
    }
    setMessage(t("briefings.clusterNotInFeed"));
  };

  // Envelope from /categories/{id}/briefing (i18n Slice 2): `body` is the
  // narrative; `language_served` powers the fallback badge.
  const briefingEnvelope = briefingQuery.data;
  const briefing = briefingEnvelope?.body ?? null;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("briefings.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden grid grid-cols-[12rem_1fr] gap-4 mt-2 min-h-0">
          <div className="overflow-y-auto space-y-1 pe-2 border-e border-border" aria-label={t("briefings.categoriesAria")}>
            {categoriesQuery.isPending ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("briefings.loading")}
              </div>
            ) : null}
            {categoriesQuery.isError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t("briefings.categoriesError")}</AlertDescription>
              </Alert>
            ) : null}
            {categories.map((category) => (
              <button
                aria-pressed={selectedCategoryId === category.category_id}
                className={`w-full text-start p-2 rounded text-sm hover:bg-muted ${
                  selectedCategoryId === category.category_id ? "bg-accent text-primary font-medium" : ""
                }`}
                key={category.category_id}
                onClick={() => {
                  setSelectedCategoryId(category.category_id);
                  setMessage(null);
                }}
                type="button"
              >
                <div><strong>{category.category_id}</strong></div>
                <div className="text-xs text-muted-foreground">{categoryTime(category)}</div>
              </button>
            ))}
          </div>
          <div className="overflow-y-auto space-y-4 pe-2">
            {briefingQuery.isPending ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("briefings.loadingDetail")}
              </div>
            ) : null}
            {briefingQuery.isError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t("briefings.detailError")}</AlertDescription>
              </Alert>
            ) : null}
            {briefing ? (
              <>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <strong className="text-sm">{briefing.category}</strong>
                    {briefingEnvelope?.language_served ? (
                      <LanguageBadge
                        requestedLanguage={language}
                        servedLanguage={briefingEnvelope.language_served}
                      />
                    ) : null}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {t("briefings.updated", { value: clockTime(briefing.updated_at) })}
                  </span>
                </div>
                <section className="space-y-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h3 className="text-xs uppercase tracking-wide text-muted-foreground cursor-help">
                        {t("briefings.keyThemes")}
                      </h3>
                    </TooltipTrigger>
                    <TooltipContent>{t("briefings.keyThemesHelp")}</TooltipContent>
                  </Tooltip>
                  {briefing.key_themes.map((theme) => (
                    <ThemeRow key={theme.theme} theme={theme} onClusterClick={handleClusterClick} />
                  ))}
                </section>
                <section className="space-y-2">
                  <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t("briefings.recentEvents")}
                  </h3>
                  {briefing.recent_events.map((event) => (
                    <Card className="p-3 space-y-2" key={`${event.event}-${event.occurred_at}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <strong className="text-sm">{event.event}</strong>
                        <span className="text-xs text-muted-foreground">{clockTime(event.occurred_at)}</span>
                      </div>
                      <p className="text-sm">{event.summary}</p>
                      <ClusterButton clusterId={event.supporting_cluster_id} onClusterClick={handleClusterClick} />
                    </Card>
                  ))}
                </section>
                <section className="space-y-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h3 className="text-xs uppercase tracking-wide text-muted-foreground cursor-help">
                        {t("briefings.activeRisks")}
                      </h3>
                    </TooltipTrigger>
                    <TooltipContent>{t("briefings.activeRisksHelp")}</TooltipContent>
                  </Tooltip>
                  {briefing.active_risks.map((risk) => (
                    <RiskRow key={risk.risk} risk={risk} onClusterClick={handleClusterClick} />
                  ))}
                </section>

                <OlderVersionsSection
                  pages={historyQuery.data?.pages ?? []}
                  isLoading={historyQuery.isPending || historyQuery.isFetching}
                  isFetchingNext={historyQuery.isFetchingNextPage}
                  hasNextPage={Boolean(historyQuery.hasNextPage)}
                  onLoadMore={() => {
                    if (historyQuery.hasNextPage && !historyQuery.isFetchingNextPage) {
                      historyQuery.fetchNextPage();
                    }
                  }}
                  currentVersion={briefing.version}
                  onClusterClick={handleClusterClick}
                />
              </>
            ) : null}
            {message ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
